import { useState, useCallback } from 'react';
import { filterCommands, SLASH_COMMANDS, type SlashCommand } from './slashCommands';

/**
 * Intercepts textarea input for slash commands.
 *
 * Trigger: input starts with "/" (no leading spaces).
 * Selection: Arrow keys navigate, Enter/Tab confirm, Escape dismisses.
 * Format commands: set a pending suffix appended to the next send().
 */
export function useSlashCommands({
  input,
  setInput,
  sendOrQueue,
  clearChat,
  setThreadsOpen,
}: {
  input: string;
  setInput: (v: string) => void;
  sendOrQueue: (text?: string) => void;
  clearChat: () => void;
  setThreadsOpen: (v: boolean | ((prev: boolean) => boolean)) => void;
}) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [formatModifier, setFormatModifier] = useState<{ suffix: string; label: string } | null>(null);

  // Picker is open when input starts with "/" and has no spaces (mid-word only)
  const slashQuery = input.startsWith('/') && !input.includes(' ')
    ? input.slice(1)
    : null;
  const pickerOpen = slashQuery !== null;
  const filteredCommands = slashQuery === '' ? SLASH_COMMANDS : filterCommands(slashQuery ?? '');

  const executeCommand = useCallback(
    (cmd: SlashCommand) => {
      if (cmd.action.type === 'send') {
        setInput('');
        setSelectedIndex(0);
        const prompt = formatModifier
          ? `${cmd.action.prompt} ${formatModifier.suffix}`
          : cmd.action.prompt;
        setFormatModifier(null);
        sendOrQueue(prompt);
      } else if (cmd.action.type === 'ui') {
        setInput('');
        setSelectedIndex(0);
        if (cmd.action.action === 'new_chat') clearChat();
        if (cmd.action.action === 'open_history') setThreadsOpen(true);
      } else if (cmd.action.type === 'format') {
        setFormatModifier({ suffix: cmd.action.suffix, label: cmd.action.label });
        setInput('');
        setSelectedIndex(0);
      }
    },
    [sendOrQueue, clearChat, setThreadsOpen, setInput, formatModifier],
  );

  /** Intercept Arrow/Enter/Tab/Escape when picker is open. Returns true if event was consumed. */
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>): boolean => {
      if (!pickerOpen || filteredCommands.length === 0) return false;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((i) => (i + 1) % filteredCommands.length);
        return true;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((i) => (i - 1 + filteredCommands.length) % filteredCommands.length);
        return true;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        const cmd = filteredCommands[selectedIndex];
        if (cmd) executeCommand(cmd);
        return true;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setInput('');
        setSelectedIndex(0);
        return true;
      }
      return false;
    },
    [pickerOpen, filteredCommands, selectedIndex, executeCommand, setInput],
  );

  const clearFormatModifier = useCallback(() => setFormatModifier(null), []);

  return {
    pickerOpen,
    slashQuery: slashQuery ?? '',
    selectedIndex,
    filteredCommands,
    formatModifier,
    clearFormatModifier,
    executeCommand,
    handleKeyDown,
  };
}
